import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X, Send, Loader2, Camera, Mic, Paperclip, Image, FileText,
  MessageCircle, Mail, MessageSquare, Maximize2, Minimize2,
} from 'lucide-react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { newIdemKey } from '../../lib/platform.js'
import { voiceSupported, createRecognizer } from '../../services/voice.js'
import { parseServerDate } from '../../utils/helpers'
import AgentAvatar from './AgentAvatar.jsx'
import AgentFormCard from './AgentFormCard.jsx'
import { useTranslation } from 'react-i18next'

// The show/hide choice is PER USER (not shared across everyone on the same browser).
export const agentHiddenKey = (user) => `agent3p-hidden:${user?.id || user?.email || 'anon'}`

/**
 * ProTrack Agent — the floating animated owl assistant (bottom-LEFT, so it
 * never overlaps the project chat panel bottom-right).
 *
 * Behaviour:
 *  • The owl floats/bobs in place and jumps when hovered.
 *  • Shortly after login it "speaks": a bubble pops up with the user's #1
 *    next action (from /ai/action-guide), exactly like a real assistant.
 *  • Clicking the owl opens the panel: one tab per door — the chat here, the conversation by
 *    email, and WhatsApp — each showing that whole conversation, one continuous thread, from
 *    where the person left off. The agent itself remembers across all three.
 *  • The panel can be resized from its free corner and edges, maximised to the full screen, and
 *    brought back to its compact size. The size is remembered per person.
 */
const AVATAR_SIZE = 64
const POS_KEY = 'agent3p-pos'
// "Minimise" is this: the compact panel the agent has always opened as.
const COMPACT = { w: 384, h: 480 }
const MIN_W = 320
const MIN_H = 360
const sizeKey = (user) => `agent3p-size:${user?.id || user?.email || 'anon'}`

const TABS = [
  { key: 'chat', label: 'Chat', icon: MessageCircle },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
]

function defaultPos() {
  // Default: bottom-RIGHT corner
  return {
    x: window.innerWidth - AVATAR_SIZE - 20,
    y: window.innerHeight - AVATAR_SIZE - 20,
  }
}

function clampPos(p) {
  return {
    x: Math.min(Math.max(p.x, 4), window.innerWidth - AVATAR_SIZE - 4),
    y: Math.min(Math.max(p.y, 4), window.innerHeight - AVATAR_SIZE - 4),
  }
}

function clampSize(s) {
  return {
    w: Math.round(Math.min(Math.max(s.w, MIN_W), window.innerWidth - 16)),
    h: Math.round(Math.min(Math.max(s.h, MIN_H), window.innerHeight - 100)),
  }
}

function dayLabel(d) {
  const day = new Date(d); day.setHours(0, 0, 0, 0)
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const diff = Math.round((today - day) / 86400000)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  return day.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

/** A stored turn, in the shape the bubbles render. parseServerDate, never new Date(): the API
 *  sends naive UTC timestamps, and new Date() reads those as local time. */
const fromServer = (m) => ({
  id: m.id, role: m.role, content: m.content, tool_name: m.tool_name, at: parseServerDate(m.at),
})

/** A small "Today" / "Mon, 28 Sep" marker wherever the day changes — the reader's own day. */
function DayMarks({ turns, render }) {
  let last = null
  return turns.map((t, i) => {
    const key = t.at ? t.at.toDateString() : null
    const mark = key && key !== last
    if (key) last = key
    return (
      <div key={t.id ?? `live-${i}`}>
        {mark && (
          <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400 my-2">
            {dayLabel(t.at)}
          </p>
        )}
        {render(t, i)}
      </div>
    )
  })
}

export default function AICopilotChat() {
  const { t: tAI } = useTranslation('assistant')
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [available, setAvailable] = useState(null)
  // The floating agent can be hidden (per Mam — it's distracting for some). The toggle
  // lives in the header; the choice is stored PER USER, so hiding it for one account never
  // hides it for another (even on the same browser). React live via a custom event.
  const { user } = useAuth()
  const [hidden, setHidden] = useState(false)
  useEffect(() => {
    const key = agentHiddenKey(user)
    const read = () => setHidden(localStorage.getItem(key) === '1')
    read()
    window.addEventListener('agent3p-visibility', read)
    return () => window.removeEventListener('agent3p-visibility', read)
  }, [user])
  const [messages, setMessages] = useState([])   // this sitting's turns: {role, content, at, links?, proposal?, form?}
  const [text, setText] = useState('')
  const [thinking, setThinking] = useState(false)
  // v3.3 (F3) AI Agent mode: perform actions (with a mandatory confirm before money steps).
  // ONE MODE. There used to be an Advisory/Agent toggle and nobody should have to know the
  // difference — the person wants an answer or an action, not a setting. Everything now goes to
  // the agent; advisory answers only when the agent is genuinely unavailable, and that fallback
  // is invisible. agentAvail is still read so a known-off agent skips a pointless round trip.
  const [agentAvail, setAgentAvail] = useState(true)
  // Which mailbox the agent answers, and whether WhatsApp is connected — said by the server.
  const [doors, setDoors] = useState({})
  const [tab, setTab] = useState('chat')
  // Each door's stored conversation. null = not loaded yet. The chat is loaded once per page;
  // email and WhatsApp each time their tab is opened, because that is where new turns arrive
  // from outside this screen.
  const [stored, setStored] = useState({ chat: null, email: null, whatsapp: null })
  // Prepared actions still waiting for a yes, so a proposal made yesterday can still be confirmed
  // from the conversation it was made in — and an emailed one from the Email tab.
  const [waiting, setWaiting] = useState([])
  const [cardNote, setCardNote] = useState({})     // confirm_token -> what happened, for the Email tab
  const [files, setFiles] = useState([])            // attachments for the agent composer
  const [usedTokens, setUsedTokens] = useState(() => new Set())
  const [listening, setListening] = useState(false)
  // Three separate hidden inputs rather than one whose attributes we mutate: `capture` has to be
  // present on the element BEFORE the click to open the camera, so switching it at click time is
  // unreliable across browsers.
  const cameraInputRef = useRef()
  const galleryInputRef = useRef()
  const filesInputRef = useRef()
  const [attachMenu, setAttachMenu] = useState(false)
  // `capture` is ignored on desktop, where it would just reopen the ordinary file dialog — so only
  // offer "Take a photo" on touch devices, where it actually opens the camera.
  const canTakePhoto = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)')?.matches
  const recRef = useRef(null)

  const toggleMic = () => {
    if (listening) { recRef.current?.stop(); return }
    const rec = createRecognizer({
      onResult: (full) => setText(full),
      onEnd: () => setListening(false),
      onError: () => setListening(false),
    })
    if (!rec) return
    recRef.current = rec
    setListening(true)
    rec.start()
  }
  const [bubble, setBubble] = useState(null)     // {title, detail, href, severity}
  const [bubbleDismissed, setBubbleDismissed] = useState(false)
  const scrollRef = useRef()

  // Matches Tailwind's md breakpoint: below this the mobile shell owns the conversation.
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const onChange = (e) => setIsSmallScreen(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // ── Draggable position (persisted) — users put the agent wherever they like ──
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(POS_KEY))
      if (saved && typeof saved.x === 'number') return clampPos(saved)
    } catch { /* ignore */ }
    return defaultPos()
  })
  const dragRef = useRef(null)  // {startX, startY, origX, origY, moved}

  // ── Size (persisted per person) and full screen ──
  const [size, setSize] = useState(COMPACT)
  const [maximized, setMaximized] = useState(false)
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(sizeKey(user)))
      setSize(saved && typeof saved.w === 'number' ? clampSize(saved) : COMPACT)
    } catch { setSize(COMPACT) }
  }, [user])

  // `opensOnClick` is true for the round button (a plain click toggles the chat) and false for
  // the panel's header, which only moves the agent — a click there must not close the chat.
  const onPointerDown = (e, opensOnClick = true) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y, moved: false }
    // While dragging, nothing on the page may be selected: the pointer sweeps across text and
    // the browser otherwise highlights all of it. Restored on release.
    const prevSelect = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    window.getSelection?.()?.removeAllRanges()
    const onMove = (ev) => {
      const d = dragRef.current
      if (!d) return
      const dx = ev.clientX - d.startX
      const dy = ev.clientY - d.startY
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) d.moved = true
      if (d.moved) setPos(clampPos({ x: d.origX + dx, y: d.origY + dy }))
    }
    const onUp = () => {
      const d = dragRef.current
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      document.body.style.userSelect = prevSelect
      if (d?.moved) {
        // Persist where the user dropped the agent
        setPos(p => { localStorage.setItem(POS_KEY, JSON.stringify(p)); return p })
      } else if (opensOnClick) {
        openChat()  // plain click → toggle the chat
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // Keep the agent on-screen when the window resizes
  useEffect(() => {
    const onResize = () => { setPos(p => clampPos(p)); setSize(s => clampSize(s)) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Anchor the panel + bubble to whichever side the avatar is on
  const onLeftHalf = pos.x + AVATAR_SIZE / 2 < window.innerWidth / 2
  const panelBottom = Math.max(window.innerHeight - pos.y + 10, 86)
  const panelStyle = {
    bottom: panelBottom,
    ...(onLeftHalf
      ? { left: Math.max(8, Math.min(pos.x, window.innerWidth - size.w - 8)) }
      : { right: Math.max(window.innerWidth - pos.x - AVATAR_SIZE, 8) }),
  }

  /** Drag the panel's free corner or edge. It is anchored at the bottom and on the owl's side,
   *  so it grows upwards and away from that side — the edge being pulled is the one that moves. */
  const startResize = (e, dir) => {
    e.preventDefault(); e.stopPropagation()
    const start = { x: e.clientX, y: e.clientY, w: size.w, h: size.h }
    const prevSelect = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    const onMove = (ev) => {
      const dx = ev.clientX - start.x
      const dy = ev.clientY - start.y
      setSize(clampSize({
        w: dir.x ? start.w + (onLeftHalf ? dx : -dx) : start.w,
        h: dir.y ? start.h - dy : start.h,
      }))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      document.body.style.userSelect = prevSelect
      setSize(s => { localStorage.setItem(sizeKey(user), JSON.stringify(s)); return s })
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const minimise = () => {
    setMaximized(false)
    setSize(COMPACT)
    localStorage.removeItem(sizeKey(user))
  }

  useEffect(() => {
    api.get('/ai/status').then(r => setAvailable(!!r.data?.available)).catch(() => setAvailable(false))
    api.get('/ai/agent/status').then(r => {
      setAgentAvail(!!r.data?.available)
      setDoors(r.data || {})
    }).catch(() => setAgentAvail(false))
  }, [])

  // Stop any in-progress voice dictation if the component unmounts.
  useEffect(() => () => { try { recRef.current?.stop() } catch { /* ignore */ } }, [])

  // The owl "speaks" — fetch the top action item and show it as a speech bubble
  useEffect(() => {
    if (available !== true || bubbleDismissed) return
    const t = setTimeout(() => {
      api.get('/ai/action-guide')
        .then(r => {
          // Only speak when there's REAL pending work. If the top item is the
          // "all caught up" (severity "ok"), stay quiet — no nagging.
          const top = r.data?.items?.[0]
          if (top && top.severity && top.severity !== 'ok') setBubble(top)
        })
        .catch(() => {})
    }, 2500)
    return () => clearTimeout(t)
  }, [available, bubbleDismissed])

  const loadDoor = useCallback(async (door) => {
    try {
      const r = await api.get('/ai/agent/history', { params: { days: 365, channel: door } })
      setStored(s => ({ ...s, [door]: (r.data?.messages || []).map(fromServer) }))
    } catch {
      setStored(s => ({ ...s, [door]: [] }))
    }
  }, [])

  const loadWaiting = useCallback(async () => {
    try {
      const r = await api.get('/ai/agent/pending')
      setWaiting(r.data?.items || [])
    } catch { setWaiting([]) }
  }, [])

  // The conversation picks up where it was left: the chat once per page, the other doors each
  // time they are opened.
  useEffect(() => {
    if (!open) return
    if (tab === 'chat' && stored.chat === null) loadDoor('chat')
    if (tab !== 'chat') loadDoor(tab)
    loadWaiting()
  }, [open, tab]) // eslint-disable-line react-hooks/exhaustive-deps

  // OPENS AT THE LATEST MESSAGE, every time: a stored conversation arrives after the first paint,
  // and a smooth scroll started before it lands stops wherever the thread happened to end. A
  // jump to the bottom once the content is there is what "carry on where you left off" needs.
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => {
      const el = scrollRef.current
      if (el) el.scrollTop = el.scrollHeight
    }, 30)
    return () => clearTimeout(t)
  }, [messages.length, open, tab, stored.chat, stored.email, stored.whatsapp, waiting.length, thinking, maximized])

  if (available === false) return null  // no API key → feature invisible
  // NOT ON A PHONE. The mobile shell already opens straight into the same conversation, so a
  // draggable mascot floating on top of it is something to move out of the way rather than
  // something to use — and on a 6-inch screen it covers the message being read. Desktop keeps
  // the owl; the phone gets the chat itself.
  if (isSmallScreen) return null
  if (hidden) return null                // user hid the agent from the header toggle

  /** Add picked files to the pending attachments, skipping ones already staged.
   *  A consultant who opens the menu twice and re-picks the same receipt shouldn't end up
   *  claiming the same expense two times. */
  const addFiles = (picked) => {
    const incoming = Array.from(picked || [])
    if (!incoming.length) return
    setFiles(prev => {
      const seen = new Set(prev.map(f => `${f.name}:${f.size}`))
      return [...prev, ...incoming.filter(f => !seen.has(`${f.name}:${f.size}`))]
    })
  }

  const say = (turn) => setMessages(prev => [...prev, { at: new Date(), ...turn }])

  const send = async (preset) => {
    const content = (preset ?? text).trim()
    if (thinking) return
    if (!content && files.length === 0) return
    setText('')
    if (agentAvail) return sendAgent(content)
    // Agent known to be off — answer the question rather than report a failure.
    if (!content) return
    const history = messages.filter(m => !m.proposal).map(m => ({ role: m.role, content: m.content }))
    say({ role: 'user', content })
    setThinking(true)
    try {
      const r = await api.post('/ai/chat', { message: content, history })
      say({ role: 'assistant', content: r.data?.answer || '…', links: r.data?.links || [] })
    } catch (e) {
      say({ role: 'assistant', content: e.response?.data?.detail || 'Sorry, something went wrong. Try again.', links: [] })
    } finally {
      setThinking(false)
    }
  }

  // ── Agent mode: perform actions; money/mutating steps require an explicit confirm ──
  const sendAgent = async (content) => {
    const history = messages.filter(m => !m.proposal).map(m => ({ role: m.role, content: m.content }))
    const attached = files
    say({ role: 'user', content: content + (attached.length ? `  📎 ${attached.length} file(s)` : '') })
    setFiles([])
    setThinking(true)
    try {
      const fd = new FormData()
      fd.append('message', content)
      fd.append('history', JSON.stringify(history))
      attached.forEach(f => fd.append('attachments', f))
      const r = await api.post('/ai/agent', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const d = r.data || {}
      if (d.available === false) {
        // Remember it, and still answer them — the advisory path needs no key beyond the same
        // one, but when it is the ACTION side that is off this keeps questions working.
        setAgentAvail(false)
        try {
          const a = await api.post('/ai/chat', { message: content, history })
          say({ role: 'assistant', content: a.data?.answer || '…', links: a.data?.links || [] })
        } catch {
          say({ role: 'assistant', content: d.answer || "The assistant isn't available right now." })
        }
      } else if (d.needs_confirm && d.proposal) {
        say({ role: 'assistant', content: d.answer || 'Please review and confirm:', proposal: d.proposal })
      } else if (d.form) {
        say({ role: 'assistant', content: d.answer || 'Please fill this in:', form: d.form })
      } else if (d.error) {
        say({ role: 'assistant', content: `⚠️ ${d.error}` })
      } else {
        say({ role: 'assistant', content: d.answer || 'Done.', links: d.result?.link ? [{ href: d.result.link, label: 'Open' }] : [] })
      }
    } catch (e) {
      say({ role: 'assistant', content: e.response?.data?.detail || 'The agent hit an error. Please try again.' })
    } finally {
      setThinking(false)
    }
  }

  const confirmProposal = async (proposal) => {
    if (usedTokens.has(proposal.confirm_token) || thinking) return
    setUsedTokens(prev => new Set(prev).add(proposal.confirm_token))
    setThinking(true)
    try {
      const fd = new FormData()
      fd.append('message', '')
      fd.append('confirm_token', proposal.confirm_token)
      fd.append('idempotency_key', newIdemKey())
      const r = await api.post('/ai/agent', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const d = r.data || {}
      say({ role: 'assistant', content: d.error ? `⚠️ ${d.error}` : (d.answer || 'Done.') })
    } catch (e) {
      say({ role: 'assistant', content: e.response?.data?.detail || 'Could not complete the action.' })
    } finally {
      setThinking(false)
      loadWaiting()
    }
  }

  const cancelProposal = (proposal) => {
    setUsedTokens(prev => new Set(prev).add(proposal.confirm_token))
    say({ role: 'assistant', content: 'Okay, cancelled — nothing was changed.' })
  }

  /** The Email tab's cards: a submission prepared from an email, confirmed or thrown away here.
   *  The same endpoint the dashboard card and the email reply use, so it runs one way however
   *  it is confirmed — and whichever comes first is the one that counts. */
  const confirmWaiting = async (item) => {
    if (usedTokens.has(item.confirm_token)) return
    setUsedTokens(prev => new Set(prev).add(item.confirm_token))
    try {
      const fd = new FormData()
      fd.append('message', 'confirm')
      fd.append('confirm_token', item.confirm_token)
      fd.append('idempotency_key', newIdemKey())
      const { data } = await api.post('/ai/agent', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setCardNote(n => ({ ...n, [item.confirm_token]: data?.error ? `⚠️ ${data.error}` : (data?.answer || 'Done.') }))
    } catch (e) {
      setCardNote(n => ({ ...n, [item.confirm_token]: e.response?.data?.detail || 'Could not complete the action.' }))
    } finally {
      loadWaiting()
    }
  }

  const discardWaiting = async (item) => {
    if (!window.confirm('Throw this away without doing it?')) return
    setUsedTokens(prev => new Set(prev).add(item.confirm_token))
    try {
      const { data } = await api.delete(`/ai/agent/pending/${item.confirm_token}`)
      setCardNote(n => ({ ...n, [item.confirm_token]: data?.detail || 'Discarded.' }))
    } catch (e) {
      setCardNote(n => ({ ...n, [item.confirm_token]: e.response?.data?.detail || 'Could not discard that.' }))
    } finally {
      loadWaiting()
    }
  }

  const openChat = () => {
    setOpen(o => !o)
    setBubble(null)
    setBubbleDismissed(true)
  }

  const severityIcon = { urgent: '❗', normal: '⏰', ok: '✅' }

  // A proposal from an earlier sitting is stored as "Proposed: …". While it is still waiting, the
  // conversation shows it with its Confirm button again, rather than as words that cannot be acted on.
  const stillWaiting = Object.fromEntries(waiting.map(w => [`Proposed: ${w.summary}`, w]))
  const chatTurns = [
    ...(stored.chat || []).map(t => {
      const w = t.role === 'assistant' && stillWaiting[t.content]
      return w ? { ...t, content: 'Waiting for your confirmation:', proposal: { confirm_token: w.confirm_token, summary: w.summary, money: w.money } } : t
    }),
    ...messages,
  ]
  const emailWaiting = waiting.filter(w => w.from_email)

  const bubbleFor = (m) => (
    <div className={`flex items-end gap-1.5 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      {m.role === 'assistant' && <AgentAvatar size={24} animated={false} className="mb-1 shrink-0" />}
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
        m.role === 'user'
          ? 'bg-indigo-600 text-white rounded-tr-sm'
          : 'bg-white border border-gray-200 text-gray-800 shadow-sm rounded-bl-sm'
      }`}>
        <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
        {m.links?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-100 flex flex-col gap-1">
            {m.links.map((l, j) => (
              <button
                key={j}
                onClick={() => { navigate(l.href); setOpen(false) }}
                className="text-left text-xs text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1"
              >
                → {l.label}
              </button>
            ))}
          </div>
        )}
        {m.proposal && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              <p className="text-[11px] font-semibold text-amber-800">{m.proposal.money ? '💰 Confirm action' : '⚙️ Confirm action'}</p>
              <p className="text-xs text-gray-700 mt-1">{m.proposal.summary}</p>
              {!usedTokens.has(m.proposal.confirm_token) ? (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => confirmProposal(m.proposal)} disabled={thinking}
                    className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-1.5 disabled:opacity-50">
                    ✓ Confirm
                  </button>
                  <button onClick={() => cancelProposal(m.proposal)}
                    className="text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-1.5">
                    Cancel
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-gray-400 mt-2 italic">{tAI('handled')}</p>
              )}
            </div>
          </div>
        )}
        {/* A real form, rendered in the chat. The person fills it in and it posts straight
            to the endpoint — signatures and bank details never go through the model. */}
        {m.form && <AgentFormCard form={m.form} />}
        {m.at && (
          <p className={`text-[10px] mt-1 ${m.role === 'user' ? 'text-indigo-200' : 'text-gray-400'}`}>
            {m.at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
            {/* Only what actually happened. A proposal carries the tool's name too, and calling
                it "acted" before anybody has said yes would claim the opposite of the truth. */}
            {m.tool_name && m.content?.startsWith('Done') && ' · done'}
          </p>
        )}
      </div>
    </div>
  )

  const loading = (
    <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  )

  const panelPlacement = maximized
    ? { top: 0, left: 0, right: 0, bottom: 0 }
    : { ...panelStyle, width: size.w, height: Math.min(size.h, window.innerHeight - panelBottom - 12) }

  return (
    <>
      {/* Speech bubble — the agent "speaks" your next action */}
      {!open && bubble && (
        <div
          className={`agent-bubble fixed z-50 max-w-xs bg-white rounded-2xl shadow-2xl border border-indigo-100 p-3.5 ${onLeftHalf ? 'rounded-bl-sm' : 'rounded-br-sm'}`}
          style={panelStyle}>
          <button
            onClick={() => { setBubble(null); setBubbleDismissed(true) }}
            className="absolute top-1.5 right-1.5 text-gray-300 hover:text-gray-500"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <p className="text-[11px] font-semibold text-indigo-600 mb-1">ProTrack Agent</p>
          <p className="text-sm text-gray-800 pr-3">
            {severityIcon[bubble.severity] || '👋'} {bubble.title}
          </p>
          {bubble.detail && <p className="text-xs text-gray-500 mt-0.5">{bubble.detail}</p>}
          <div className="flex gap-2 mt-2">
            {bubble.href && (
              <button
                onClick={() => { navigate(bubble.href); setBubble(null); setBubbleDismissed(true) }}
                className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3 py-1.5"
              >
                Take me there →
              </button>
            )}
            <button
              onClick={openChat}
              className="text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg px-3 py-1.5"
            >
              Ask me
            </button>
          </div>
          {/* bubble tail pointing at the owl */}
          <div className={`absolute -bottom-2 w-4 h-4 bg-white border-b border-r border-indigo-100 rotate-45 ${onLeftHalf ? 'left-5' : 'right-5'}`} />
        </div>
      )}

      {/* The floating owl — draggable; click = open chat */}
      <div
        onPointerDown={(e) => onPointerDown(e, true)}
        className="fixed z-50 cursor-grab active:cursor-grabbing touch-none select-none"
        style={{ left: pos.x, top: pos.y }}
        title={tAI('t_agent_drag')}
        aria-label="ProTrack Agent"
        role="button"
      >
        {open ? (
          <div className="w-12 h-12 rounded-full bg-gray-700 shadow-xl flex items-center justify-center hover:bg-gray-600">
            <X className="w-5 h-5 text-white" />
          </div>
        ) : (
          <AgentAvatar size={AVATAR_SIZE} animated state={bubble ? 'greeting' : 'idle'} />
        )}
      </div>

      {/* The panel — anchored to wherever the user parked the agent, or the whole screen */}
      {open && (
        <div className={`fixed flex flex-col shadow-2xl border border-gray-200 overflow-hidden bg-white ${
          maximized ? 'z-60' : 'z-50 rounded-2xl'}`}
             style={{ ...panelPlacement, maxWidth: maximized ? undefined : 'calc(100vw - 16px)' }}>
          {/* Resize from the free side: the corner, the top edge and the side edge. */}
          {!maximized && (
            <>
              <div onPointerDown={(e) => startResize(e, { x: true, y: true })}
                title="Drag to resize"
                className={`absolute top-0 z-30 w-4 h-4 ${onLeftHalf ? 'right-0 cursor-nesw-resize' : 'left-0 cursor-nwse-resize'}`} />
              <div onPointerDown={(e) => startResize(e, { x: false, y: true })}
                className="absolute top-0 left-4 right-4 h-1.5 z-30 cursor-ns-resize" />
              <div onPointerDown={(e) => startResize(e, { x: true, y: false })}
                className={`absolute top-4 bottom-0 w-1.5 z-30 cursor-ew-resize ${onLeftHalf ? 'right-0' : 'left-0'}`} />
            </>
          )}

          <div
            onPointerDown={(e) => { if (!maximized && !e.target.closest('button')) onPointerDown(e, false) }}
            className={`bg-linear-to-r from-indigo-600 to-purple-600 px-4 py-2.5 flex items-center gap-2.5 shrink-0 select-none touch-none ${
              maximized ? '' : 'cursor-grab active:cursor-grabbing'}`}>
            <AgentAvatar size={34} animated={false} />
            <div className="min-w-0 flex-1">
              <p className="text-white font-semibold text-sm leading-tight">ProTrack Agent</p>
              <p className="text-indigo-200 text-[10px] truncate">Ask me, or tell me what to do — you confirm anything that matters</p>
            </div>
            {(maximized || size.w !== COMPACT.w || size.h !== COMPACT.h) && (
              <button onClick={minimise} title="Minimise — back to the compact size"
                className="shrink-0 p-1.5 rounded-lg text-indigo-100 hover:bg-white/15">
                <Minimize2 className="w-4 h-4" />
              </button>
            )}
            {!maximized && (
              <button onClick={() => setMaximized(true)} title="Maximise — full screen"
                className="shrink-0 p-1.5 rounded-lg text-indigo-100 hover:bg-white/15">
                <Maximize2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => { setOpen(false); setMaximized(false) }} title="Close"
              className="shrink-0 p-1.5 rounded-lg text-indigo-100 hover:bg-white/15">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* One tab per door into the same agent. */}
          <div className="flex border-b border-gray-200 bg-white shrink-0">
            {TABS.map((door) => {
              const Icon = door.icon
              return (
                <button
                  key={door.key}
                  onClick={() => setTab(door.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium border-b-2 -mb-px ${
                    tab === door.key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {door.label}
                  {door.key === 'email' && emailWaiting.length > 0 && (
                    <span className="ml-0.5 text-[10px] font-semibold bg-violet-600 text-white rounded-full px-1.5">{emailWaiting.length}</span>
                  )}
                  {door.key === 'whatsapp' && !doors.whatsapp && (
                    <span className="ml-0.5 text-[9px] uppercase tracking-wide text-gray-400">soon</span>
                  )}
                </button>
              )
            })}
          </div>

          <div ref={scrollRef} className={`flex-1 overflow-y-auto px-3 py-3 bg-gray-50 space-y-3 ${maximized ? 'md:px-[12%]' : ''}`}>
            {tab === 'chat' && (
              <>
                {stored.chat === null && loading}
                {stored.chat !== null && chatTurns.length === 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-center pt-1 pb-2">
                      <AgentAvatar size={56} animated />
                    </div>
                    <p className="text-xs text-gray-400 text-center">{tAI('ask_me_anything')}</p>
                    {['What should I do next?', 'Which invoices are pending?', 'How are my projects doing?'].map(q => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        className="w-full text-left text-xs bg-white border border-indigo-100 text-indigo-700 rounded-lg px-3 py-2 hover:bg-indigo-50 transition"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
                <DayMarks turns={chatTurns} render={bubbleFor} />
                {thinking && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 pl-2">
                    <AgentAvatar size={28} animated={false} state="thinking" />
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" /> The agent is thinking…
                  </div>
                )}
              </>
            )}

            {tab === 'email' && (
              <>
                {stored.email === null && loading}
                {stored.email !== null && stored.email.length === 0 && (
                  <p className="text-sm text-gray-500 py-6 text-center">
                    Nothing by email yet. Send your work or a question
                    {doors.email_address ? <> to <b>{doors.email_address}</b></> : ''} — it will appear here.
                  </p>
                )}
                <DayMarks turns={stored.email || []} render={bubbleFor} />
                {emailWaiting.map(item => (
                  <div key={item.confirm_token} className="rounded-xl bg-violet-50 border border-violet-200 px-3 py-2.5">
                    <p className="text-[11px] font-semibold text-violet-800">Waiting for your confirmation</p>
                    <p className="text-xs text-gray-800 mt-1 whitespace-pre-wrap">{item.summary}</p>
                    {item.subject && <p className="text-[10px] text-violet-700 mt-1">from your email — “{item.subject}”</p>}
                    {cardNote[item.confirm_token] ? (
                      <p className="text-xs text-gray-700 mt-2">{cardNote[item.confirm_token]}</p>
                    ) : (
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => confirmWaiting(item)} disabled={usedTokens.has(item.confirm_token)}
                          className="text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg px-3 py-1.5 disabled:opacity-50">
                          ✓ Confirm
                        </button>
                        <button onClick={() => discardWaiting(item)} disabled={usedTokens.has(item.confirm_token)}
                          className="text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-1.5 disabled:opacity-50">
                          Discard
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            {tab === 'whatsapp' && (
              <>
                {stored.whatsapp === null && loading}
                <DayMarks turns={stored.whatsapp || []} render={bubbleFor} />
                {stored.whatsapp !== null && stored.whatsapp.length === 0 && (
                  <div className="text-center py-8 px-4">
                    <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-gray-700">WhatsApp is coming soon</p>
                    <p className="text-xs text-gray-500 mt-1">
                      When it is connected you will be able to message the agent on WhatsApp, and that
                      conversation will appear here — the same agent, remembering what you said in the
                      chat and by email.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {tab === 'email' && (
            <div className="border-t border-gray-100 bg-white px-3 py-2.5 shrink-0 text-[11px] text-gray-500">
              {doors.email_address ? (
                <>
                  To carry on, email <b className="text-gray-700">{doors.email_address}</b>
                  {doors.email_subject_marker && <> with <b className="text-gray-700">{doors.email_subject_marker}</b> at the start of the subject (this is the test copy)</>}
                  {' '}— or reply to any of its emails. Your files can go as they are: no need to name anything.
                </>
              ) : (
                'Email is not connected on this system yet.'
              )}
            </div>
          )}

          {tab === 'chat' && (
            <div className="border-t border-gray-100 bg-white p-2.5 shrink-0">
              {files.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
                      📎 {f.name.length > 18 ? f.name.slice(0, 16) + '…' : f.name}
                      <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-gray-600">×</button>
                    </span>
                  ))}
                </div>
              )}
              {/* The camera and mic sit INSIDE the input field, so the composer reads as one control
                  rather than a row of loose buttons. The field itself carries the border and focus
                  ring (focus-within), and the textarea inside is borderless. */}
              <div className="flex items-end gap-2">
                <div className="flex-1 flex items-end gap-1 border border-gray-200 rounded-xl px-1.5 py-1 bg-white focus-within:ring-2 focus-within:ring-indigo-500">
                  {/* Camera, gallery and device files are separate inputs so each opens the right
                      picker. All accept MULTIPLE items — a consultant usually has several receipts. */}
                  <input
                    ref={cameraInputRef} type="file" multiple accept="image/*"
                    capture="environment" className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = '' }}
                  />
                  <input
                    ref={galleryInputRef} type="file" multiple accept="image/*" className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = '' }}
                  />
                  <input
                    ref={filesInputRef} type="file" multiple
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = '' }}
                  />
                  <div className="relative shrink-0">
                    <button
                      onClick={() => setAttachMenu(m => !m)}
                      title={tAI('t_attach_files')}
                      className={`p-1.5 rounded-lg shrink-0 ${attachMenu ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-50'}`}
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                    {attachMenu && (
                      <>
                        {/* Click-away layer, so the menu closes without a document listener. */}
                        <div className="fixed inset-0 z-40" onClick={() => setAttachMenu(false)} />
                        <div className="absolute bottom-full left-0 mb-2 z-50 w-52 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
                          {canTakePhoto && (
                            <button onClick={() => { setAttachMenu(false); cameraInputRef.current?.click() }}
                              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50 text-left">
                              <Camera className="w-4 h-4 text-gray-400 shrink-0" /> Take a photo
                            </button>
                          )}
                          <button onClick={() => { setAttachMenu(false); galleryInputRef.current?.click() }}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50 text-left border-t border-gray-50">
                            <Image className="w-4 h-4 text-gray-400 shrink-0" /> Photos
                          </button>
                          <button onClick={() => { setAttachMenu(false); filesInputRef.current?.click() }}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50 text-left border-t border-gray-50">
                            <FileText className="w-4 h-4 text-gray-400 shrink-0" /> Files from this device
                          </button>
                          <p className="px-3 py-1.5 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100">
                            You can pick several at once.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {voiceSupported() && (
                    <button
                      onClick={toggleMic}
                      title={listening ? 'Stop dictation' : 'Speak your request'}
                      className={`p-1.5 rounded-lg shrink-0 ${listening ? 'text-white bg-red-500 animate-pulse' : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-50'}`}
                    >
                      <Mic className="w-4 h-4" />
                    </button>
                  )}
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
                    placeholder="Ask, or tell me what to do…"
                    rows={1}
                    className="flex-1 min-w-0 resize-none text-sm bg-transparent border-0 px-1.5 py-1.5 focus:outline-none focus:ring-0 max-h-24"
                  />
                </div>
                <button
                  onClick={() => send()}
                  disabled={thinking || (!text.trim() && files.length === 0)}
                  className="p-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-40 shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}
